import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ResetPassword.page.css';
import MainLogo from '../../../ui/components/MainLogo';

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError(
        'Invalid or missing reset token. Please request a new password reset.'
      );
      return;
    }

    if (!email) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          token,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      setSuccess(
        data.message ||
          'Password has been reset successfully! Redirecting to sign in...'
      );
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    } catch (err: any) {
      setError(
        err.message || 'An error occurred while resetting your password'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <MainLogo nav={navigate} />
      <div className="reset-password-box">
        <h2 className="reset-password-title">Reset Password</h2>
        <p className="reset-password-description">
          Enter your new password below.
        </p>

        {error && <div className="reset-password-error">{error}</div>}
        {success && <div className="reset-password-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="password"
              id="newPassword"
              className="reset-password-input"
              placeholder="New Password (min. 8 characters)"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError('');
                setSuccess('');
              }}
              required
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              id="confirmPassword"
              className="reset-password-input"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
                setSuccess('');
              }}
              required
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            className="reset-password-button"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <p className="reset-password-links">
          Remember your password? <a href="/signin">Sign In</a>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
